import { useCallback, useMemo, useRef } from 'react';
import type { SyntheticEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { TimeInput, type TimeInputRef } from './TimeInput';

interface DurationInputProps {
  name?: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  minSeconds?: number;
  maxSeconds?: number;
}

interface TimeParts {
  days: number;
  hours: number;
  minutes: number;
}

// Convert days/hours/minutes to seconds
export const timeToSeconds = (time: TimeParts): number => {
  return time.days * 86400 + time.hours * 3600 + time.minutes * 60;
};

// Convert seconds to days/hours/minutes
export const secondsToTime = (seconds: number): TimeParts => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return { days, hours, minutes };
};

export const DurationInput = ({
  value,
  onChange,
  disabled,
  className,
  minSeconds = 300,
  maxSeconds = 14 * 86400,
}: DurationInputProps) => {
  const { t } = useTranslation();

  const seconds = value;
  const minTotal = Math.max(minSeconds, 60);
  const maxTotal = Math.max(maxSeconds, minTotal);
  const { days, hours, minutes } = secondsToTime(seconds);

  const daysRef = useRef<TimeInputRef>(null);
  const hoursRef = useRef<TimeInputRef>(null);
  const minutesRef = useRef<TimeInputRef>(null);

  // Memoized dynamic bounds (single set) based on current state and totals
  const dynBounds = useMemo(() => {
    const daysMax = Math.max(0, Math.floor((maxTotal - hours * 3600 - minutes * 60) / 86400));
    const daysMin = Math.max(0, Math.ceil((minTotal - (23 * 3600 + 59 * 60)) / 86400));

    const hoursMax = Math.max(
      0,
      Math.min(23, Math.floor((maxTotal - days * 86400 - minutes * 60) / 3600))
    );
    const hoursMin = Math.max(
      0,
      Math.min(23, Math.ceil((minTotal - days * 86400 - 59 * 60) / 3600))
    );

    const minutesMax = Math.max(
      0,
      Math.min(59, Math.floor((maxTotal - days * 86400 - hours * 3600) / 60))
    );
    const minutesMin = Math.max(
      0,
      Math.min(59, Math.ceil((minTotal - days * 86400 - hours * 3600) / 60))
    );

    const bounds = {
      days: { min: daysMin, max: daysMax },
      hours: { min: hoursMin, max: hoursMax },
      minutes: { min: minutesMin, max: minutesMax },
    } as const;

    return bounds;
  }, [days, hours, minutes, minTotal, maxTotal]);

  const handleDaysChange = useCallback(
    (value: number) => {
      const base = secondsToTime(seconds);

      // Check if total exceeds min or max, and set to total min/max accordingly
      let totalSeconds = timeToSeconds({
        days: Math.max(0, Math.min(365, value)),
        hours: base.hours,
        minutes: base.minutes,
      });
      if (totalSeconds > maxTotal) {
        totalSeconds = maxTotal;
        daysRef.current?.triggerShake();
      }

      onChange(totalSeconds);
    },
    [seconds, maxTotal, onChange]
  );

  const handleHoursChange = useCallback(
    (value: number, event: SyntheticEvent<HTMLInputElement>) => {
      const isTyping = event.type === 'change';
      // Cap at 23 to not increment the days field while typing
      const hoursCap = isTyping ? 23 : 24;
      const base = { days, hours: value, minutes };

      // Check if total exceeds min or max, and set to total min/max accordingly
      let totalSeconds = timeToSeconds({
        days: base.days,
        hours: Math.max(0, Math.min(hoursCap, value)),
        minutes: base.minutes,
      });
      if (totalSeconds > maxTotal) {
        totalSeconds = maxTotal;
        hoursRef.current?.triggerShake();
      }

      onChange(totalSeconds);
    },
    [days, minutes, maxTotal, onChange]
  );

  const handleMinutesChange = useCallback(
    (value: number, event: SyntheticEvent<HTMLInputElement>) => {
      const isTyping = event.type === 'change';
      // Cap at 59 to not increment the hours field while typing
      const minutesCap = isTyping ? 59 : 60;
      const base = secondsToTime(seconds);

      // Check if total exceeds min or max, and set to total min/max accordingly
      let totalSeconds = timeToSeconds({
        days: base.days,
        hours: base.hours,
        minutes: Math.max(0, Math.min(minutesCap, value)),
      });
      if (totalSeconds > maxTotal) {
        totalSeconds = maxTotal;
        minutesRef.current?.triggerShake();
      }

      onChange(totalSeconds);
    },
    [seconds, maxTotal, onChange]
  );

  const handleDaysBlur = useCallback(() => {
    if (seconds < minTotal) {
      onChange(minTotal);
      daysRef.current?.triggerShake();
      return;
    }
    if (seconds > maxTotal) {
      onChange(maxTotal);
      daysRef.current?.triggerShake();
    }
  }, [seconds, minTotal, maxTotal, onChange]);

  const handleHoursBlur = useCallback(() => {
    if (seconds < minTotal) {
      onChange(minTotal);
      hoursRef.current?.triggerShake();
      return;
    }
    if (seconds > maxTotal) {
      onChange(maxTotal);
      hoursRef.current?.triggerShake();
    }
  }, [seconds, minTotal, maxTotal, onChange]);

  const handleMinutesBlur = useCallback(() => {
    if (seconds < minTotal) {
      onChange(minTotal);
      minutesRef.current?.triggerShake();
      return;
    }
    if (seconds > maxTotal) {
      onChange(maxTotal);
      minutesRef.current?.triggerShake();
    }
  }, [seconds, minTotal, maxTotal, onChange]);

  return (
    <div className={cn('mt-6 flex items-end gap-6', className)}>
      <TimeInput
        ref={daysRef}
        label={t('common.days')}
        value={days}
        onChange={handleDaysChange}
        onBlur={handleDaysBlur}
        min={dynBounds.days.min}
        disabled={disabled}
        onArrowRight={() => hoursRef.current?.focus()}
      />
      <TimeInput
        ref={hoursRef}
        label={t('common.hours')}
        value={hours}
        onChange={handleHoursChange}
        onBlur={handleHoursBlur}
        min={dynBounds.hours.min}
        disabled={disabled}
        onArrowLeft={() => daysRef.current?.focus()}
        onArrowRight={() => minutesRef.current?.focus()}
      />
      <TimeInput
        ref={minutesRef}
        label={t('common.minutes')}
        value={minutes}
        onChange={handleMinutesChange}
        onBlur={handleMinutesBlur}
        min={dynBounds.minutes.min}
        disabled={disabled}
        onArrowLeft={() => hoursRef.current?.focus()}
      />
    </div>
  );
};

export default DurationInput;
